import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  tabs: [],
  activeTab: null,
  forms: {
    suppliers: {
      companyName: '',
      legalName: '',
      address: '',
      taxNumber: 0,
      phone: 0,
      creditPeriod: 0,
      creditLimit: 0,
      comment: '',
      whagent: true,
      all_products: false,
      isCredit: false,
      payment_flow_type: 'NORMAL',
      abasto_bank_name: '',
      abasto_agreement_name: '',
      abasto_agreement_number: '',
      abasto_interest_rate: '',
    },
    orders: {
      dateTime: '',
      comment: '',
      total: 0,
      subTotal: 0,
      bonusDiscount: 0,
      priceDiscount: 0,
      currency: 'Q',
      storage: '',
      warehouse_id: null,
      commentSupplier: '',
      supplier_id: 0,
      order_details: [],
    },
    products: {
      category: '',
      code: '',
      description: '',
      prformat: '',
      units: 0,
      supplier_id: 0,
      price: {
        price: 0,
        priceType: '',
      }
    },
    earlyPayment: {
      // Future: pronto pago fields
    },
    invoice: {
      date: null,
      total: 0,
      invoiceType: 'FC',
      invoiceStatus: 'ISSF',
      number: null,
      series: '',
      paymentType: true,
      paymentStatus: 'PENDING',
      supplier_id: 0,
      signedById: null,
      invoice_detail: [],
      invoiceDocuments: null,
    },
  },
  flows: {
    order: {
      activeStep: 0,
      completed: {},
      selectedProducts: [],
      orderDetails: [],
      selectedPromotions: [],
      currentOrderDetailIndex: null,
    },
    invoice: {
      activeStep: 0,
      completed: {},
      hasOrder: false,
      supplierId: null,
      selectionModel: [],
    },
  },
};

const flattenInvoice = (invoice) => {
  if (!invoice) return invoice;
  return {
    ...invoice,
    number: String(invoice.number || ''),
    series: String(invoice.series || ''),
    invoice_type: invoice.invoice_type?.id ? { id: invoice.invoice_type.id } : null,
    invoice_detail: invoice.invoice_detail?.map(item => ({
      ...item,
      product: undefined,
      priceOptions: undefined,
    })) || [],
    invoiceDocumentsPreview: undefined,
  };
};

const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    addTab: (state, action) => {
      const { label, path } = action.payload;
      const existingTab = state.tabs.find((tab) => tab.path === path);
      if (!existingTab) {
        state.tabs.push({ label, path });
      }
      // Solo actualizar activeTab si realmente cambia de ruta
      if (!state.activeTab || state.activeTab.path !== path) {
        state.activeTab = existingTab || { label, path };
      }
    },
    closeTab: (state, action) => {
      const tabToRemove = action.payload;
      state.tabs = state.tabs.filter((tab) => tab.path !== tabToRemove.path);

      if (state.activeTab?.path === tabToRemove.path) {
        const idx = state.tabs.findIndex((tab) => tab.path === tabToRemove.path);
        state.activeTab = state.tabs[idx - 1] || state.tabs[idx + 1] || null;
      }
    },
    setActiveTabByPath: {
      reducer(state, action) {
        const path = action.payload;
        if (path === state.activeTab?.path) return;
        if (path === null) {
          state.activeTab = null;
        } else {
          const tab = state.tabs.find((t) => t.path === path);
          state.activeTab = tab || null;
        }
      },
      prepare(path) {
        return { payload: path };
      },
    },
    updateFormData: (state, action) => {
      const { formName, field, value } = action.payload;
      if (!state.forms[formName]) state.forms[formName] = {};
      state.forms[formName][field] = value;
    },
    setForm: (state, action) => {
      const { formName, data } = action.payload;
      const payloadData = formName === 'invoice' ? flattenInvoice(data) : data;
      state.forms[formName] = { ...(state.forms[formName] || {}), ...payloadData };
    },
    replaceForm: (state, action) => {
      const { formName, data } = action.payload;
      const payloadData = formName === 'invoice' ? flattenInvoice(data) : data;
      state.forms[formName] = payloadData || {};
    },
    resetFormData: (state, action) => {
      const { formName } = action.payload;
      if (Object.prototype.hasOwnProperty.call(initialState.forms, formName)) {
        state.forms[formName] = JSON.parse(JSON.stringify(initialState.forms[formName]));
      } else {
        state.forms[formName] = {};
      }
    },
    setFlowField: (state, action) => {
      const { name, field, value } = action.payload;
      if (!state.flows[name]) state.flows[name] = {};
      if (name === 'invoice' && field === 'invoice') {
        const cleanInvoice = {
          ...value,
          invoice_detail: value.invoice_detail?.map(item => ({
            product_id: item.product_id?.id || item.product_id,
            quantity: item.quantity,
            quantityRe: item.quantityRe,
            price: typeof item.price === 'object' ? item.price.price : item.price,
            description: typeof item.description === 'object' 
              ? item.description.description || '' 
              : item.description,
            code: typeof item.code === 'object' ? item.code.code || '' : item.code,
          })) || []
        };
        state.flows[name][field] = cleanInvoice;
      } else {
        state.flows[name][field] = value;
      }
    },
    mergeFlow: (state, action) => {
      const { name, data } = action.payload;
      state.flows[name] = { ...(state.flows[name] || {}), ...data };
    },
    clearFlow: (state, action) => {
      const name = action.payload;
      if (!name) return;
      if (Object.prototype.hasOwnProperty.call(state.flows, name)) {
        delete state.flows[name];
      }
    },
    resetFlow: (state, action) => {
      const { name } = action.payload;
      if (Object.prototype.hasOwnProperty.call(initialState.flows, name)) {
        state.flows[name] = JSON.parse(JSON.stringify(initialState.flows[name]));
      } else {
        state.flows[name] = {};
      }
    },
    clearAllTabs: (state) => {
      state.tabs = [];
      state.activeTab = null;
    },
    clearWorkspaceState: (state) => {
      state.tabs = [];
      state.activeTab = null;
      state.forms = JSON.parse(JSON.stringify(initialState.forms));
      state.flows = JSON.parse(JSON.stringify(initialState.flows));
    },
  },
});

export const {
  addTab,
  closeTab,
  setActiveTabByPath,
  updateFormData,
  resetFormData,
  setForm,
  replaceForm,
  setFlowField,
  mergeFlow,
  clearFlow,
  resetFlow,
  clearAllTabs,
  clearWorkspaceState,
} = tabsSlice.actions;

export default tabsSlice.reducer;
